import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
  Alert,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { getUniqueCurrencies } from "../../utils/currencyOptions";

const shopPrimaryGreen = "#355d31";
const shopPrimaryGreenDark = "#274923";

const PayrollAdd = ({ open, onClose, token, onSaved }) => {
  const { t } = useTranslation();
  const { handleAdd, loading, resetForm } = useAdd("shop/payrolls", token);
  const auth = useAuth();
  const selectedBranch = auth?.user?.selected_branch ? String(auth.user.selected_branch) : "";

  // Fetch foreign key data: shops, employees, and currencies.
  const { data: shopsData } = useFetchObjects(
    ["shop"],
    "shop/shop/?page_size=1000",
    token
  );
  const { data: employeesData, isLoading: employeeLoading } = useFetchObjects(
    ["employee"],
    "shop/employees/?page_size=1000",
    token
  );
  const { data: currenciesData, isLoading: currencyLoading } = useFetchObjects(
    ["currency"],
    "shop/currencies?page_size=all",
    token
  );

  const shops = shopsData?.results || shopsData || [];
  const employees = employeesData?.results || employeesData || [];
  const currencies = currenciesData?.results || currenciesData || [];

  const [selectedShop, setSelectedShop] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [amount, setAmount] = useState("");
  const [exchangeRate, setExchangeRate] = useState("");
  const [salary, setSalary] = useState(""); // To hold the employee's salary
  const [paymentDate, setPaymentDate] = useState("");
  const [monthYear, setMonthYear] = useState("");
  const [notes, setNotes] = useState("");
  const { data: accountsData, isLoading: accountLoading } = useFetchObjects(
    ["accounts", "payroll-add", selectedShop || selectedBranch],
    selectedShop || selectedBranch ? `shop/accounts/?page_size=all&shop=${selectedShop || selectedBranch}` : "",
    token
  );
  const accounts = Array.isArray(accountsData) ? accountsData : accountsData?.results || [];
  const selectedAccount = useMemo(
    () => accounts.find((acc) => String(acc.id) === String(selectedAccountId)) || null,
    [accounts, selectedAccountId]
  );
  const sameCurrencyAccounts = useMemo(
    () => accounts.filter((acc) => String(acc.currency) === String(selectedCurrency)),
    [accounts, selectedCurrency]
  );
  const employeeOptions = useMemo(
    () =>
      selectedShop
        ? employees.filter(
            (emp) =>
              String(emp.shop) === String(selectedShop) ||
              String(emp.shop_details?.id) === String(selectedShop)
          )
        : employees,
    [employees, selectedShop]
  );
  const currencyOptions = useMemo(
    () =>
      getUniqueCurrencies(
        selectedShop
          ? currencies.filter((cur) => String(cur.shop) === String(selectedShop))
          : currencies
      ),
    [currencies, selectedShop]
  );
  const safeSelectedShop = shops.some((shop) => String(shop.id) === String(selectedShop))
    ? selectedShop
    : "";
  const safeSelectedEmployee = employeeOptions.some(
    (employee) => String(employee.id) === String(selectedEmployee)
  )
    ? selectedEmployee
    : "";
  const safeSelectedCurrency = currencyOptions.some(
    (currency) => String(currency.id) === String(selectedCurrency)
  )
    ? selectedCurrency
    : "";
  const effectiveShopId =
    safeSelectedShop ||
    selectedShop ||
    selectedBranch ||
    (shops[0]?.id ? String(shops[0].id) : "");

  useEffect(() => {
    if (selectedShop) return;
    if (selectedBranch) {
      setSelectedShop(selectedBranch);
      return;
    }
    if (shops.length > 0) {
      setSelectedShop(String(shops[0].id));
    }
  }, [selectedBranch, selectedShop, shops]);

  useEffect(() => {
    if (!selectedCurrency || !effectiveShopId) {
      setSelectedAccountId("");
      return;
    }
    if (accounts.length === 0) {
      setSelectedAccountId("");
      return;
    }
    const exists = accounts.some((acc) => String(acc.id) === String(selectedAccountId));
    if (!exists) {
      const preferred = sameCurrencyAccounts[0] || accounts[0];
      setSelectedAccountId(preferred ? String(preferred.id) : "");
    }
  }, [selectedCurrency, effectiveShopId, accounts, sameCurrencyAccounts, selectedAccountId]);

  const { data: directRateData } = useFetchObjects(
    ["payroll-rate-direct", effectiveShopId, selectedCurrency, selectedAccount?.currency],
    effectiveShopId && selectedCurrency && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${effectiveShopId}&from_currency=${selectedCurrency}&to_currency=${selectedAccount.currency}`
      : "",
    token
  );
  const { data: reverseRateData } = useFetchObjects(
    ["payroll-rate-reverse", effectiveShopId, selectedCurrency, selectedAccount?.currency],
    effectiveShopId && selectedCurrency && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${effectiveShopId}&from_currency=${selectedAccount.currency}&to_currency=${selectedCurrency}`
      : "",
    token
  );
  const exchangeRatePreview = useMemo(() => {
    if (!selectedCurrency) return "";
    const accountCurrencyId = selectedAccount?.currency ?? selectedCurrency;
    if (String(selectedCurrency) === String(accountCurrencyId)) return "1";
    const direct = directRateData?.results?.[0]?.rate;
    if (direct) return String(direct);
    const reverse = reverseRateData?.results?.[0]?.rate;
    if (reverse && Number(reverse) > 0) return String(1 / Number(reverse));
    return "";
  }, [selectedCurrency, selectedAccount, directRateData, reverseRateData]);
  useEffect(() => {
    setExchangeRate(exchangeRatePreview);
  }, [exchangeRatePreview]);
  const convertedAmount = useMemo(() => {
    const nAmount = Number(amount);
    const nRate = Number(exchangeRate);
    if (!Number.isFinite(nAmount) || !Number.isFinite(nRate) || nAmount <= 0 || nRate <= 0) {
      return "";
    }
    return (nAmount * nRate).toFixed(2);
  }, [amount, exchangeRate]);

  // Update salary when employee is selected
  useEffect(() => {
    if (selectedEmployee) {
      const employee = employees.find(
        (emp) => emp.id === parseInt(selectedEmployee)
      );
      setSalary(employee ? employee.salary : "");
      setAmount(employee ? employee.salary : ""); // Auto-fill amount with salary
    }
  }, [selectedEmployee, employees]);

  useEffect(() => {
    if (
      selectedEmployee &&
      !employeeOptions.some((employee) => String(employee.id) === String(selectedEmployee))
    ) {
      setSelectedEmployee("");
      setSalary("");
      setAmount("");
    }
  }, [selectedEmployee, employeeOptions]);

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append("shop", effectiveShopId);
    formData.append("employee", selectedEmployee);
    formData.append("currency", selectedCurrency);
    formData.append("account", selectedAccountId);
    formData.append("amount", amount);
    formData.append("exchange_rate", exchangeRate);
    if (monthYear) {
      const [year, month] = monthYear.split("-");
      formData.append("year", year);
      formData.append("month", month);
    }
    formData.append("payment_date", paymentDate);
    formData.append("notes", notes);
    handleAdd(formData);
  };

  useEffect(() => {
    if (resetForm) {
      setSelectedShop(selectedBranch || (shops[0]?.id ? String(shops[0].id) : ""));
      setSelectedEmployee("");
      setSelectedCurrency("");
      setSelectedAccountId("");
      setAmount("");
      setExchangeRate("");
      setSalary("");
      setPaymentDate("");
      setMonthYear("");
      setNotes("");
      onSaved?.();
      onClose();
    }
  }, [resetForm, onClose, onSaved, selectedBranch, shops]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("payroll.addPayroll")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            select
            margin="dense"
            label={t("payroll.selectEmployee")}
            fullWidth
            required
            value={safeSelectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
          >
            <MenuItem value="">{t("payroll.selectEmployee")}</MenuItem>
            {employeeLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              employeeOptions.map((emp) => (
                <MenuItem key={emp.id} value={String(emp.id)}>
                  {emp.name || "--"}
                </MenuItem>
              ))
            )}
          </TextField>

          {selectedEmployee && (
            <TextField
              margin="dense"
              label={t("payroll.employeeSalary")}
              type="text"
              fullWidth
              value={salary || "--"}
              InputProps={{
                readOnly: true,
              }}
            />
          )}

          <TextField
            select
            margin="dense"
            label={t("payroll.selectCurrency")}
            fullWidth
            required
            value={safeSelectedCurrency}
            onChange={(e) => setSelectedCurrency(e.target.value)}
          >
            <MenuItem value="">{t("payroll.selectCurrency")}</MenuItem>
            {currencyLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              currencyOptions.map((cur) => (
                <MenuItem key={cur.id} value={String(cur.id)}>
                  {cur.title || "--"}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            margin="dense"
            label={t("payroll.amount")}
            type="number"
            fullWidth
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />

          <TextField
            select
            margin="dense"
            label={t("payroll.account", "Account")}
            fullWidth
            required
            value={selectedAccountId}
            onChange={(e) => setSelectedAccountId(e.target.value)}
            disabled={!effectiveShopId || !selectedCurrency || accountLoading}
          >
            {accountLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : accounts.length === 0 ? (
              <MenuItem value="" disabled>
                {t("payroll.noAccount", "No accounts found")}
              </MenuItem>
            ) : (
              accounts.map((acc) => (
                <MenuItem key={acc.id} value={String(acc.id)}>
                  {acc.name} ({acc.currency_title || "-"})
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            margin="dense"
            label={t("payroll.exchangeRate", "Exchange Rate")}
            type="number"
            fullWidth
            required
            value={exchangeRate}
            onChange={(e) => setExchangeRate(e.target.value)}
            inputProps={{ min: 0, step: "0.000001" }}
          />

          <TextField
            margin="dense"
            label={t("payroll.convertedAmount", "Converted Amount")}
            fullWidth
            value={convertedAmount}
            disabled
          />

          {!exchangeRatePreview && selectedCurrency && selectedAccountId && !exchangeRate && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {t("payroll.missingRate", "No exchange rate found for selected account currency. You can enter it manually.")}
            </Alert>
          )}

          {salary && amount && (
            <TextField
              margin="dense"
              label={t("payroll.remainingAmount")}
              type="text"
              fullWidth
              value={(parseFloat(salary) - parseFloat(amount)).toFixed(2)}
              InputProps={{
                readOnly: true,
                style: {
                  color: (parseFloat(salary) - parseFloat(amount)) < 0 ? 'red' : 'green'
                }
              }}
            />
          )}

          <TextField
            margin="dense"
            label={t("payroll.monthYear", "Month/Year")}
            type="month"
            fullWidth
            required
            value={monthYear}
            onChange={(e) => setMonthYear(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
          />

          <TextField
            margin="dense"
            label={t("payroll.paymentDate")}
            type="datetime-local"
            fullWidth
            required
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
          />

          <TextField
            margin="dense"
            label={t("payroll.notes")}
            fullWidth
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("payroll.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
            disabled={
              loading ||
            !effectiveShopId ||
            !selectedEmployee ||
            !selectedCurrency ||
            !amount ||
            !selectedAccountId ||
            !exchangeRate ||
            !monthYear ||
            !paymentDate
          }
          sx={{
            minWidth: 100,
            backgroundColor: shopPrimaryGreen,
            "&:hover": {
              backgroundColor: shopPrimaryGreenDark,
            },
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("payroll.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PayrollAdd;
